import React from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import { Alert } from '@carsys/ui'
import { useArrowScrollHandler } from '@carsys/ui/src/helpers/keyboard/use-arrow-scroll-handler'
import { useFilter } from 'app/hooks/useFilter'
import { BrandCard } from './BrandCard'
import Box from '@mui/material/Box'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { SearchFilter } from '../../fields/SearchFilter'

export interface IBrand {
  name: string
  key: string
  value: string
}

interface ISelectedBrands {
  brands: IBrand[]
  selectedKey?: string
  onBrandSelect: (key: string, value: IBrand) => void
  hideSearch?: boolean
}

const useColumnCount = () => {
  const theme = useTheme()
  const lg = useMediaQuery(theme.breakpoints.up('lg'))
  const md = useMediaQuery(theme.breakpoints.up('md'))
  return lg ? 6 : md ? 3 : 2
}

export const BrandCardList: React.FC<ISelectedBrands> = ({ brands, selectedKey, onBrandSelect, hideSearch }) => {
  const { t } = useTranslation()
  const [filteredTableData, setFilter, filter] = useFilter(brands)
  const columnCount = useColumnCount()
  const { currentFocus } = useArrowScrollHandler(filteredTableData.length, columnCount)
  return (
    <Stack spacing={2} sx={{ minHeight: 0 }}>
      {!hideSearch && (
        <Box data-testid='brandselection'>
          <SearchFilter filter={filter} setFilter={setFilter} placeholder={t('Vehicle.SearchBrand')} name='brand-selection-search' />
        </Box>
      )}
      <Box sx={{ minHeight: 0, overflow: 'auto' }}>
        <Grid container spacing={1} data-testid='select-card-grid'>
          {filteredTableData.map((brand, index: number) => (
            <BrandCard
              brand={brand}
              key={index}
              onBrandSelect={onBrandSelect}
              selectedKey={selectedKey}
              focus={currentFocus === index || filteredTableData.length === 1}
            />
          ))}
          {filteredTableData.length === 0 && (
            <Grid item xs={12}>
              <Alert severity='info' data-testid='no-results'>
                {t('global.search_no_results_found')}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </Stack>
  )
}
